import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VotingPage() {
  const navigate = useNavigate();
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(null);
  const sid = localStorage.getItem("lumis_session_id");

  useEffect(() => { loadVotes(); }, []);

  const loadVotes = async () => {
    try {
      const res = await axios.get(`${API}/votes?session_id=${sid}`);
      setVotes(res.data.votes);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleVote = async (mod) => {
    if (mod.status === "active" || voting) return;
    setVoting(mod.module_id);
    try {
      await axios.post(`${API}/votes/${mod.module_id}?session_id=${sid}`);
      await loadVotes();
    } catch (e) { console.error(e); }
    finally { setVoting(null); }
  };

  const locked = votes.filter(m => m.status !== "active");
  const maxVotes = Math.max(...locked.map(m => m.vote_count), 1);
  const myVotes = votes.filter(m => m.user_voted).length;

  if (loading) return (
    <div className="min-h-screen bg-[#EEF2FF] flex items-center justify-center">
      <div className="text-[#1E3A8A] text-3xl animate-pulse font-outfit">✦</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#EEF2FF] flex flex-col max-w-md mx-auto" data-testid="voting-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#EEF2FF]/90 backdrop-blur-xl border-b border-gray-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <button data-testid="voting-back-btn" onClick={() => navigate("/modules")} className="p-2 rounded-full hover:bg-gray-100 transition-all text-gray-500">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-outfit font-light text-gray-900">Próximos Módulos</h1>
            <p className="text-gray-500 text-xs font-manrope">Sua voz define o futuro do Lumis</p>
          </div>
        </div>
      </div>

      <div className="flex-1 p-5 space-y-4 pb-10">
        {myVotes > 0 && (
          <div className="bg-white border border-[#1E3A8A]/15 rounded-2xl px-4 py-3 flex items-center gap-3 lumis-fade">
            <span className="text-[#1E3A8A] text-sm">✦</span>
            <p className="text-gray-500 text-sm font-manrope">
              Você votou em <span className="text-[#1E3A8A]">{myVotes} módulo{myVotes > 1 ? "s" : ""}</span>
            </p>
          </div>
        )}

        {locked.map((mod, i) => (
          <div
            key={mod.module_id}
            className="bg-white border border-gray-200 rounded-3xl p-4 lumis-fade"
            style={{ animationDelay: `${i * 0.06}s` }}
            data-testid={`vote-module-${mod.module_id}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-gray-900 text-sm font-outfit font-medium truncate">{mod.name}</h3>
                <p className="text-gray-500 text-xs font-manrope mt-0.5">{mod.vote_count} votos</p>
              </div>
              <button
                data-testid={`vote-btn-${mod.module_id}`}
                onClick={() => handleVote(mod)}
                disabled={!!voting}
                className={`shrink-0 ml-3 px-4 py-2 rounded-full text-xs font-manrope font-medium transition-all active:scale-[0.95] disabled:cursor-wait ${
                  mod.user_voted
                    ? "bg-[#DBEAFE] text-[#1E3A8A] border border-[#1E3A8A]/25"
                    : "bg-[#1E3A8A] text-white hover:bg-[#172554]"
                }`}
              >
                {voting === mod.module_id ? "..." : mod.user_voted ? (
                  <span className="flex items-center gap-1"><Check size={11} /> Votado</span>
                ) : "Votar"}
              </button>
            </div>
            <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#1E3A8A]/50 rounded-full transition-all duration-500"
                style={{ width: `${Math.max(4, (mod.vote_count / maxVotes) * 100)}%` }}
              />
            </div>
          </div>
        ))}

        <p className="text-center text-gray-500/40 text-xs font-manrope pt-2">
          Os módulos mais votados serão desenvolvidos primeiro.
        </p>
      </div>
    </div>
  );
}
